import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, Book1, Headphone, MessageQuestion } from 'iconsax-reactjs';
import type { FC } from 'react';
import { EmptyLayout } from '@/base/layouts/EmptyLayout';
import { PageMeta } from '@/base/PageMeta';
import { Link } from '@/base/router';
import { useExternalLinks } from '@/base/useExternalLinks';
import { LanguageSelect } from '@/settings/LanguageSelect';
import { ThemeSelect } from '@/settings/ThemeSelect';
import { Button } from '@/shared/Button';
import { Logo } from '@/shared/Logo';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';

export const ContactPage: FC = () => {
  const { t } = useLingui();
  const links = useExternalLinks();
  usePupeteerSignal(true);

  return (
    <EmptyLayout>
      <PageMeta
        title={t`Contact Us`}
        description={t`Get in touch with IQX support. We're here to help with any questions about trading crypto in Iraq.`}
        localizedPath="/contact-us"
      />
      <div className="min-h-svh w-full flex flex-col bg-background-primary">
        <header className="flex justify-between items-center p-3 w-full shrink-0">
          <Link to="/">
            <Logo className="h-10" text />
          </Link>
          <div className="flex gap-2 items-center shrink-0">
            <LanguageSelect
              mini
              size={8}
              arrow={false}
              className="w-auto px-2"
            />
            <ThemeSelect size={8} arrow={false} className="px-0 !w-8" mini />
          </div>
        </header>

        <main className="flex-1 flex flex-col items-center justify-center p-4 desktop:p-6 max-w-lg mx-auto w-full">
          <div className="w-full flex flex-col gap-widget">
            <Link to="/" className="contents">
              <Button size={6} variant="text" className="self-start">
                <ArrowLeft size={22} className="auto-rtl" /> {t`Back`}
              </Button>
            </Link>

            <h1 className="text-2xl desktop:text-3xl font-semibold">
              {t`Contact us`}
            </h1>
            <p className="text-base text-content-tertiary">
              {t`Need help? Reach out through any of the options below.`}
            </p>

            <div className="flex flex-col gap-3 pt-2">
              <Button
                size={10}
                type="div"
                variant="brand"
                block
                className="w-full justify-start gap-3"
                onClick={() => links.support()}
              >
                <Headphone size={22} className="shrink-0" />
                {t`Contact Support`}
              </Button>

              <a
                href={links.helpCenter}
                target="_blank"
                referrerPolicy="no-referrer"
                className="contents"
                rel="noopener"
              >
                <Button
                  size={10}
                  type="div"
                  variant="outline"
                  block
                  className="w-full justify-start gap-3"
                >
                  <Book1 size={22} className="shrink-0" />
                  {t`Help Center`}
                </Button>
              </a>

              <a
                href={links.faq}
                target="_blank"
                referrerPolicy="no-referrer"
                className="contents"
                rel="noopener"
              >
                <Button
                  size={10}
                  type="div"
                  variant="outline"
                  block
                  className="w-full justify-start gap-3"
                >
                  <MessageQuestion size={22} className="shrink-0" />
                  {t`FAQ`}
                </Button>
              </a>
            </div>

            <p className="text-sm text-content-tertiary pt-2">
              {t`We typically respond within 24 hours. For urgent matters, use the support chat above.`}
            </p>
          </div>
        </main>
      </div>
    </EmptyLayout>
  );
};
